type ErrorWithCode = Error & { code?: string };

const describeError = (error: ErrorWithCode) =>
  error.code && !error.message.includes(error.code) ? `${error.code} ${error.message}` : error.message;

/**
 * Describes why a request could not be sent, from the `cause` that Node's fetch attaches to its "fetch failed"
 * error: a DNS failure, a refused or timed-out connection, a TLS error. A connection tried on several addresses
 * fails with an AggregateError, whose errors name each address.
 */
export const describeFetchFailure = (error: unknown): string | undefined => {
  const cause = (error as { cause?: unknown } | null)?.cause;
  if (!(cause instanceof Error)) {
    return undefined;
  }

  if (cause instanceof AggregateError && cause.errors.length > 0) {
    const attempts = cause.errors.filter((e): e is ErrorWithCode => e instanceof Error).map(describeError);
    return `${(cause as ErrorWithCode).code ?? cause.name} (${attempts.join('; ')})`;
  }

  return describeError(cause);
};

/**
 * fetch for the RTK Query base query. fetchBaseQuery reports a failed request as String(error), which for Node's
 * fetch is only "TypeError: fetch failed"; the reason is moved into the message here so the logged error keeps it.
 */
export const fetchWithCause: typeof fetch = async (input, init) => {
  try {
    return await fetch(input, init);
  } catch (error) {
    const failure = error instanceof TypeError ? describeFetchFailure(error) : undefined;
    if (failure) {
      throw new TypeError(`${error instanceof Error ? error.message : 'fetch failed'}: ${failure}`, { cause: error });
    }
    throw error;
  }
};
